import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import type { Contact } from "@/types/contact";

const contactSchema = z.object({
  name: z.string().min(1, "Nome obrigatório"),
  phone_number: z.string().optional(),
  identifier: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => Promise<void>;
  contact?: Contact | null;
}

export function ContactForm({
  open,
  onClose,
  onSubmit,
  contact,
}: ContactFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: contact
      ? {
          name: contact.name,
          phone_number: contact.phone_number || "",
          identifier: contact.identifier || "",
        }
      : { name: "", phone_number: "", identifier: "" },
  });

  useEffect(() => {
    if (open) {
      reset(
        contact
          ? {
              name: contact.name,
              phone_number: contact.phone_number || "",
              identifier: contact.identifier || "",
            }
          : { name: "", phone_number: "", identifier: "" },
      );
    }
  }, [open, contact, reset]);

  const handle = async (data: ContactFormData) => {
    await onSubmit(data);
    reset();
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{contact ? "Editar contato" : "Novo contato"}</SheetTitle>
          <SheetDescription>
            {contact
              ? "Altere os dados do contato abaixo."
              : "Preencha os dados para criar um novo contato."}
          </SheetDescription>
        </SheetHeader>
        <form
          onSubmit={handleSubmit(handle)}
          className="flex flex-1 flex-col gap-4 px-4"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              {...register("name")}
              placeholder="Nome completo"
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone_number">Telefone</Label>
            <Input
              id="phone_number"
              {...register("phone_number")}
              placeholder="+55 11 99999-9999"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="identifier">Identificador</Label>
            <Input
              id="identifier"
              {...register("identifier")}
              placeholder="Opcional"
            />
          </div>
          <SheetFooter className="mt-auto">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
